/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useRef } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {
  Card, CardContent, TextField, IconButton, MenuItem, Select, Typography, Fab, Tooltip,
} from '@material-ui/core';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import CloseIcon from '@material-ui/icons/Close';
import RecordVoiceOverIcon from '@material-ui/icons/RecordVoiceOver';

const useStyles = makeStyles((theme) => ({
  widget: {
    position: 'fixed',
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    width: 'fit-content',
    maxWidth: 400,
    minWidth: 200,
    zIndex: 2000,
    boxShadow: theme.shadows[5],
    borderRadius: '12px',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    zIndex: 2100,
  },

  mobileTab: {
    position: 'fixed',
    bottom: theme.spacing(2),
    left: 0,
    width: 30,
    height: 100,
    backgroundColor: theme.palette.primary.main,
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2100,
    color: theme.palette.primary.contrastText,
    cursor: 'pointer',
    boxShadow: theme.shadows[4],
    userSelect: 'none',
  },
  mobileIcon: {
    fontSize: '1.4rem',
  },
}));

const allowedVoiceNames = ['en-GB'];

export default function PronunciationWidget() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [word, setWord] = useState('');
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const loadVoices = () => {
      const allVoices = speechSynthesis.getVoices();
      const filtered = allVoices.filter((v) => allowedVoiceNames.includes(v.lang));
      setVoices(filtered);
      if (filtered.length && !selectedVoice) {
        setSelectedVoice(filtered[0].name);
      }
    };
    loadVoices();
    speechSynthesis.onvoiceschanged = loadVoices;
  }, [selectedVoice]);

  const handleSpeak = () => {
    if (!word.trim()) return;
    const utterance = new SpeechSynthesisUtterance(word);
    const voice = voices.find((v) => v.name === selectedVoice);
    if (voice) utterance.voice = voice;
    speechSynthesis.speak(utterance);
  };

  if (!open) {
    return (
      <>
        {!isMobile ? (
          <Tooltip title="Pronunciation Box" arrow>
            <Fab
              color="primary"
              className={classes.fab}
              onClick={() => setOpen(true)}
            >
              <RecordVoiceOverIcon />
            </Fab>
          </Tooltip>
        ) : (
          <div className={classes.mobileTab} onClick={() => setOpen(true)}>
            <RecordVoiceOverIcon className={classes.mobileIcon} />
          </div>
        )}
      </>
    );
  }

  return (
    <Card className={classes.widget} ref={containerRef}>
      <CardContent>
        <div className={classes.titleRow}>
          <Typography variant="subtitle1">
            Pronunciation Box
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </div>

        <Select
          value={selectedVoice}
          onChange={(e) => setSelectedVoice(e.target.value)}
          fullWidth
          size="small"
          MenuProps={{
            container: containerRef.current,
            disablePortal: true,
          }}
        >
          {voices.map((voice, index) => (
            <MenuItem key={voice.name} value={voice.name}>
              {`voice ${index + 1}`}
              {' '}
              (
              {voice.lang}
              )
            </MenuItem>
          ))}
        </Select>

        <div className={classes.inputRow}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Word in English"
            value={word}
            onChange={(e) => setWord(e.target.value)}
            fullWidth
          />
          <IconButton color="primary" onClick={handleSpeak}>
            <VolumeUpIcon />
          </IconButton>
        </div>
      </CardContent>
    </Card>
  );
}
